// src/views/UserViews/insights/components/SectionCard.tsx
//
// Shared card chrome for Insights chart blocks (ComplianceTrendChart, DeviceStatusDonut,
// VersionDistributionChart) so they share one header treatment instead of each hand-rolling
// its own `Typography variant="h6"` heading. Fleet Status uses this with an empty `title`,
// the current-status subtitle, `autoHeight`, a one-step-darker `borderColor` so the
// wrapper reads apart from the nested MetricCards, and `subtitleVariant="body1"` so that
// one subtitle is a bit larger than other SectionCard subtitles. Safety no longer exists
// as a page section (see Insights.tsx).
//
// `title` is typed as a plain `string` (not optional) because every real usage always has
// one, but the header row itself only renders when `title`/`titleAdornment`/`subtitle`/`action`
// is non-empty - this lets ComplianceTrendChart's hidden single-day state reuse this same card
// chrome with `title=""` and no header row, so the hidden message doesn't introduce a heading
// where none existed before (its own test asserts `queryByRole('heading')` is null in that state).
//
// `autoHeight` (opt-in, default false): every existing caller (ComplianceTrendChart's own
// chart-only branches, DeviceStatusDonut, VersionDistributionChart) keeps `height: '100%'`,
// which lets a CSS Grid row's default `align-items: stretch` (see Insights.tsx) hand this
// card the row's full height - each of those callers' own inner content is a SINGLE `flex: 1`
// child that is designed to grow/center into that stretched height, so stretching is exactly
// what they want. `autoHeight: true` (used by ComplianceTrendChart when its per-feature
// strip is rendered - see that file - and by FleetStatusSection so the KPI row does not
// stretch like chart cards) instead sizes the card to its own content: dropping
// `height: '100%'` and setting `alignSelf: 'start'` opts this one grid item out of stretch
// entirely, so its resolved height stays `auto` (undetermined) rather than becoming a
// definite length equal to the row's height.
//
// Why that distinction matters - confirmed in a real browser render, not assumed from CSS
// spec text: with a definite height imposed by stretch, a `flex: 1` descendant (the chart
// wrapper Box inside ComplianceTrendChart) genuinely activates flex-grow and expands to
// consume ALL of that definite height, pushing a second stacked sibling (the feature-
// compliance strip, rendered after it in the same flex column) past the bottom of that fixed
// height - which `Card`'s own `overflow: hidden` (applied for its rounded corners) then
// clips. Measured: a 516.5px-tall stretched card clipped the strip's value text at 532.5px
// (16px, exactly the sliver visible in the bug screenshot), while the identical content
// rendered with no definite ancestor height at all (this file's `autoHeight` case) came out
// to the SAME 516.5px card height purely from its own content, with the value's bottom at
// 492.5px - comfortably inside, no clipping. `alignSelf: 'start'` is what makes that
// difference: it is a no-op outside a flex/grid parent, so it is safe to always include in
// the `autoHeight` variant regardless of which container ends up rendering this card.

import type { ReactNode } from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import {
  INSIGHTS_CARD_BORDER_COLOR,
  INSIGHTS_CARD_BORDER_RADIUS,
  INSIGHTS_CARD_BOX_SHADOW,
} from './cardSurfaceStyles';

// This card's border/radius/shadow (via `cardSurfaceStyles`) and its `CardContent` padding
// below used to come from a GLOBAL `MuiCard`/`MuiCardContent` theme default. That default
// applied to every `<Card>` in the app, not just this one - it overrode `elevation={0}` on
// unrelated screens (Organization Info's transparent outer wrapper in particular, since
// `defaultProps` beats an instance's own `elevation`) and shifted their `CardContent`
// padding by 4px. Both are applied locally here instead now - do NOT re-globalize this into
// `theme.ts`'s `MuiCard`/`MuiCardContent`, that is the exact regression this guards against.
// MUI's own `CardContent` default last-child bottom padding is 24px; PADDING_PX below is
// used for all four sides (including last-child) to match the original, deleted default.
const PADDING_PX = 20;

type SectionCardSubtitleVariant = 'body1' | 'body2';

type SectionCardProps = {
  title: string;
  /** Optional status/help content rendered beside, but outside, the heading element. */
  titleAdornment?: ReactNode;
  /** Plain string for every chart card; Fleet Status passes a bold+italic React node. */
  subtitle?: ReactNode;
  /** Default `body2` keeps every other Insights subtitle at its current size. Fleet
   * Status passes `body1` so its current-status line is a bit more readable. */
  subtitleVariant?: SectionCardSubtitleVariant;
  action?: ReactNode;
  headingLevel?: 'h2' | 'h3';
  /** See header comment. Default `false` preserves every other caller's existing
   * stretch-to-fill-the-grid-row behavior unchanged. */
  autoHeight?: boolean;
  /** Default is the shared Insights card token. Fleet Status overrides so the outer
   * wrapper is a modestly darker gray than the nested MetricCards. */
  borderColor?: string;
  children: ReactNode;
};

export default function SectionCard({
  title,
  titleAdornment,
  subtitle,
  subtitleVariant = 'body2',
  action,
  headingLevel = 'h2',
  autoHeight = false,
  borderColor = INSIGHTS_CARD_BORDER_COLOR,
  children,
}: SectionCardProps) {
  const hasHeader = Boolean(title || titleAdornment || subtitle || action);

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor,
        borderRadius: `${INSIGHTS_CARD_BORDER_RADIUS}px`,
        boxShadow: INSIGHTS_CARD_BOX_SHADOW,
        ...(autoHeight ? { alignSelf: 'start' } : { height: '100%' }),
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: `${PADDING_PX}px`,
          '&:last-child': { paddingBottom: `${PADDING_PX}px` },
        }}
      >
        {hasHeader && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              {(title || titleAdornment) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {title && (
                    <Typography
                      variant="subtitle1"
                      component={headingLevel}
                      sx={{ fontWeight: 700 }}
                    >
                      {title}
                    </Typography>
                  )}
                  {titleAdornment}
                </Box>
              )}
              {subtitle && (
                <Typography variant={subtitleVariant} color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            {action}
          </Box>
        )}
        {/* `flex: 1` so a chart wrapper below can itself flex-grow to fill the card's real
         * height (see ComplianceTrendChart/DeviceStatusDonut) - `display: flex` here makes
         * this a flex container for that single child, not just a flex item of CardContent,
         * so the child's own `flex: 1` has something to resolve against. `minHeight: 0`
         * overrides the flex default `min-height: auto`, which would otherwise floor this
         * box at its content's height and defeat the shrink half of "grows and shrinks with
         * the card". */}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );
}
